// =============================================================================
// iTravel 2.0 — useResolvedTheme Hook
// =============================================================================
// Resolves the user's theme mode preference ('light' | 'dark' | 'auto') into
// a concrete MUI Theme object. When mode is 'auto', the system's
// prefers-color-scheme media query is used to determine light vs dark.
//
// Usage:
//   const theme = useResolvedTheme();
//   <ThemeProvider theme={theme}>…</ThemeProvider>
// =============================================================================

import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo } from 'react';

import type { ThemeMode } from '@/store/theme.store';
import { selectThemeMode, useThemeStore } from '@/store/theme.store';
import { darkTheme, lightTheme } from '@/theme/theme';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Given the user-selected mode and the system preference, return the concrete
 * palette mode ('light' | 'dark') that should be applied.
 */
function resolveMode(userMode: ThemeMode, systemPrefersDark: boolean): 'light' | 'dark' {
  if (userMode === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return userMode;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Returns the resolved MUI Theme based on the user's theme mode preference
 * and (when set to 'auto') the operating system's color scheme preference.
 *
 * The hook is reactive:
 * - Changing the theme mode in the store triggers a re-render with the new theme.
 * - When mode is 'auto', toggling the OS dark mode also triggers a re-render.
 */
export function useResolvedTheme(): Theme {
  const userMode = useThemeStore(selectThemeMode);

  // This media query is live — it updates if the user changes their OS theme
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true,
  });

  const theme = useMemo(() => {
    const resolved = resolveMode(userMode, systemPrefersDark);
    return resolved === 'dark' ? darkTheme : lightTheme;
  }, [userMode, systemPrefersDark]);

  return theme;
}

/**
 * Returns the resolved palette mode string ('light' | 'dark') — useful when
 * you need the mode label without the full Theme object.
 */
export function useResolvedMode(): 'light' | 'dark' {
  const userMode = useThemeStore(selectThemeMode);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true,
  });

  return resolveMode(userMode, systemPrefersDark);
}
